<template>
    <div class="tip-summary-table">
        <table class="total">
            <tr>
                <td class="name">{{ translations.pay.consumption }}</td>
                <td class="currency">{{ translations.currency.chf }}</td>
                <td class="amount">{{ toMoney(totalConsumption, true) }}</td>
            </tr>
            <tr v-if="showFees">
                <td class="name">{{ translations.pay.fees }}</td>
                <td class="currency">{{ translations.currency.chf }}</td>
                <td class="amount">{{ toMoney(fees, true) }}</td>
            </tr>
            <tr
                v-if="hasDiscounts"
                class="discount"
                @click="onDiscountClick"
                :class="{ clickable: isDiscountClickable }"
            >
                <td class="name">{{ translations.pay.totalDiscount }}</td>
                <td class="currency discount">{{ translations.currency.chf }}</td>
                <td class="amount discount">{{ toMoney(-1 * discountAmount, true) }}</td>
            </tr>
            <tr v-if="showTip">
                <td class="name">{{ translations.pay.tip }}</td>
                <td class="currency">{{ translations.currency.chf }}</td>
                <td class="amount">{{ toMoney(tip ?? 0, true) }}</td>
            </tr>
        </table>
        <!-- own table, so that the bigger kiosk text does not widen the columns of the
             rows above. rendered only when a total is passed in, which is kiosk only -->
        <table v-if="total != null" class="total total-to-pay">
            <tr>
                <td class="name">{{ translations.pay.total }}</td>
                <td class="currency">{{ translations.currency.chf }}</td>
                <td class="amount">{{ toMoney(total, true) }}</td>
            </tr>
        </table>
    </div>
</template>

<script setup lang="ts">
    import { translations } from "@/shared/localization/translations";
    import { toMoney } from "@/shared/utils/format";

    // define props
    const props = withDefaults(
        defineProps<{
            totalConsumption: number;
            discountAmount: number;
            fees: number; // only delivery fee
            hasDiscounts: boolean;
            tip: number | null;
            showTip: boolean;
            showFees?: boolean;
            isDiscountClickable?: boolean;
            // the amount actually charged. passed in rather than summed from the rows
            // above, so it always matches what the caller charges. omitted -> no row
            total?: number | null;
        }>(),
        { showFees: true }
    );

    const emit = defineEmits(["discountClick"]);

    function onDiscountClick() {
        if (props.isDiscountClickable) {
            emit("discountClick");
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .tip-summary-table {
        font-size: 16px;
        font-weight: 300;

        padding: 0.75em 0.375em 1.125em;
        border-top: 1px solid colors.$light-gray-100;

        table.total {
            width: 100%;
            line-height: 1.5em;
            color: colors.$fontColor;

            td.name {
                width: 100%;
            }

            td.currency {
                padding: 0 1em;

                &.discount {
                    color: colors.$pink;
                }
            }

            tr.discount.clickable {
                cursor: pointer;
                transition: opacity 0.2s variables.$easeOutExpo;

                &:hover {
                    opacity: 0.5;
                }
            }

            td.amount {
                text-align: right;

                &.discount {
                    color: colors.$pink;
                }
            }
        }
    }
</style>
